/* eslint-disable max-len */
/**
 * Pricing Index Dashboard — data layer
 *
 * GROUNDING POLICY
 * ----------------
 * All index values are real BLS data, retrieved from data.bls.gov and stored
 * in bls-real-data.ts. Source URLs and official series titles are exposed
 * alongside each card.
 *
 * The §24.4.5 weight basket reproduces the exact figures from the MEPPI
 * "Attachment B — Pricing related Terms for Suppliers" workbook, sheet `v1`,
 * rows 10–14 (sum = 1.0 confirmed in the source).
 *
 * Where MEPPI's operational data is not yet known (per-supplier baskets,
 * supplier identities, claim history, current-bid SKUs) the dashboard renders
 * an explicit placeholder state — never a fabricated value.
 */

import {
  REAL_BLS_DATA,
  BLS_SOURCE_URLS,
  BLS_OFFICIAL_TITLES
} from './bls-real-data';

export type DataSource = 'bls' | 'contract' | 'derived' | 'placeholder' | 'tbd';

export interface IndexSeriesPoint {
  date: string;
  value: number;
}

export interface PricingIndex {
  code: string;
  label: string;             // human-readable label for the dashboard
  officialTitle: string;     // exact BLS title
  blsSeriesId: string;
  measures: string;
  frequency: 'Monthly' | 'Quarterly';
  sourceUrl: string;
  series: IndexSeriesPoint[];
}

export interface SupplierIndexLink {
  indexCode: string;
  weight: number;
  weightSource: DataSource;
}

export interface SupplierContract {
  id: string;
  name: string;
  segment: string;
  baseDate: string;
  basePrice: number;
  unitLabel: string;
  nextReviewDate: string;
  contractRef: string;
  reviewCadence: string;
  passThroughPct: number;
  passThroughSource: DataSource;
  contractDataSource: DataSource;
  indices: SupplierIndexLink[];
  pendingClaim?: {
    claimedPct: number;
    receivedDate: string;
    rationale: string;
    source: DataSource;
  };
  pastClaims: PastClaim[];
}

export interface PastClaim {
  date: string;
  claimedPct: number;
  indexJustifiedPct: number;
  agreedPct: number | null;
  outcome: 'accepted' | 'partial' | 'rejected' | 'pending';
  note: string;
}

export interface InsightCard {
  tone: 'positive' | 'warning' | 'neutral';
  headline: string;
  body: string;
}

export interface BidSku {
  partNumber: string;
  description: string;
  dataPoints: number;
  matchedIndex: string;
  unitCostEstimate: number;
  indexMovement12mo: number;
  coverage: 'covered' | 'partial' | 'uncovered';
  recommendedBufferPct: number;
  rationale: string;
}

// ─── Indices ────────────────────────────────────────────────────────────────
// Six BLS series defined: the five §24.4.5 basket members (WPU10, WPUID61113,
// CEU3133530008, CEU3133590008, CEU3133600008) plus ECI Wages, which is
// referenced in trend commentary but is NOT a basket member. All carry real
// BLS values pulled from authoritative sources — no synthetic data.

export const INDICES: PricingIndex[] = [
  {
    code: 'WPU10',
    label: 'WPU10 — Metals & Metal Products PPI',
    officialTitle: BLS_OFFICIAL_TITLES['WPU10'],
    blsSeriesId: 'WPU10',
    measures: 'Producer Price Index for the metals & metal products commodity group (steel, aluminium, copper, fasteners).',
    frequency: 'Monthly',
    sourceUrl: BLS_SOURCE_URLS['WPU10'],
    series: REAL_BLS_DATA['WPU10']
  },
  {
    code: 'WPUID61113',
    label: 'WPUID61113 — Materials, Durable Mfg PPI',
    officialTitle: BLS_OFFICIAL_TITLES['WPUID61113'],
    blsSeriesId: 'WPUID61113',
    measures: 'PPI for inputs to durable-goods manufacturing — broad basket covering plastics, rubber, machinery sub-assemblies.',
    frequency: 'Monthly',
    sourceUrl: BLS_SOURCE_URLS['WPUID61113'],
    series: REAL_BLS_DATA['WPUID61113']
  },
  {
    code: 'ECIWAG',
    label: 'ECIWAG — ECI Wages, Private Industry',
    officialTitle: BLS_OFFICIAL_TITLES['ECIWAG'],
    blsSeriesId: 'CIU2010000000000A',
    measures: 'Wages & salaries component of the Employment Cost Index, used as proxy for the contract-named CIU2020000120000I per the MEPPI workbook footnote.',
    frequency: 'Quarterly',
    sourceUrl: BLS_SOURCE_URLS['ECIWAG'],
    series: REAL_BLS_DATA['ECIWAG']
  },
  {
    code: 'CEU3133530008',
    label: 'CEU3133530008 — AHE Electrical Equipment',
    officialTitle: BLS_OFFICIAL_TITLES['CEU3133530008'],
    blsSeriesId: 'CEU3133530008',
    measures: 'Average hourly earnings of production workers in NAICS 3353 (Electrical Equipment Manufacturing), not seasonally adjusted.',
    frequency: 'Monthly',
    sourceUrl: BLS_SOURCE_URLS['CEU3133530008'],
    series: REAL_BLS_DATA['CEU3133530008']
  },
  {
    code: 'CEU3133590008',
    label: 'CEU3133590008 — AHE Other Electrical Equipment',
    officialTitle: BLS_OFFICIAL_TITLES['CEU3133590008'],
    blsSeriesId: 'CEU3133590008',
    measures: 'Average hourly earnings of production workers in NAICS 3359 (Other Electrical Equipment & Component Mfg).',
    frequency: 'Monthly',
    sourceUrl: BLS_SOURCE_URLS['CEU3133590008'],
    series: REAL_BLS_DATA['CEU3133590008']
  },
  {
    code: 'CEU3133600008',
    label: 'CEU3133600008 — AHE Transportation Equipment',
    officialTitle: BLS_OFFICIAL_TITLES['CEU3133600008'],
    blsSeriesId: 'CEU3133600008',
    measures: 'Average hourly earnings of production workers in NAICS 336 (Transportation Equipment Manufacturing).',
    frequency: 'Monthly',
    sourceUrl: BLS_SOURCE_URLS['CEU3133600008'],
    series: REAL_BLS_DATA['CEU3133600008']
  },
  {
    code: 'CEU3133200008',
    label: 'CEU3133200008 — AHE Fabricated Metal Mfg',
    officialTitle: BLS_OFFICIAL_TITLES['CEU3133200008'],
    blsSeriesId: 'CEU3133200008',
    measures: 'Average hourly earnings of production workers in NAICS 332 (Fabricated Metal Product Manufacturing), not seasonally adjusted. Real BLS series.',
    frequency: 'Monthly',
    sourceUrl: BLS_SOURCE_URLS['CEU3133200008'],
    series: REAL_BLS_DATA['CEU3133200008']
  },
  {
    code: 'CEU3133320008',
    label: 'CEU3133320008 — AHE Industrial Machinery Mfg',
    officialTitle: BLS_OFFICIAL_TITLES['CEU3133320008'],
    blsSeriesId: 'CEU3133320008',
    measures: 'Average hourly earnings of production workers in NAICS 3332 (Industrial Machinery Manufacturing), not seasonally adjusted. Real BLS series.',
    frequency: 'Monthly',
    sourceUrl: BLS_SOURCE_URLS['CEU3133320008'],
    series: REAL_BLS_DATA['CEU3133320008']
  },
  {
    code: 'PCU332710332710',
    label: 'PCU332710332710 — PPI Machine Shops',
    officialTitle: BLS_OFFICIAL_TITLES['PCU332710332710'],
    blsSeriesId: 'PCU332710332710',
    measures: 'Producer Price Index for NAICS 332710 — Machine Shops industry. Gold-standard index for precision-machining cost escalation.',
    frequency: 'Monthly',
    sourceUrl: BLS_SOURCE_URLS['PCU332710332710'],
    series: REAL_BLS_DATA['PCU332710332710']
  }
];

// ─── §24.4.5 contract basket — verbatim from Attachment B v1 rows 10-14 ────
// Source: MEPPI "Attachment B — Pricing related Terms for Suppliers" workbook,
// sheet `v1`, rows 10–14. Sum = 1.0 (confirmed by the source).

export const CONTRACT_BASKET_24_4_5: { indexCode: string; weight: number; clauseLabel: string }[] = [
  { indexCode: 'WPU10',         weight: 0.30, clauseLabel: 'WPU10 Index Weighting' },
  { indexCode: 'WPUID61113',    weight: 0.30, clauseLabel: 'WPUID61113 Index Weighting' },
  { indexCode: 'CEU3133530008', weight: 0.15, clauseLabel: 'Electrical Equipment Index Weighting' },
  { indexCode: 'CEU3133590008', weight: 0.05, clauseLabel: 'Other Electrical Index Weighting' },
  { indexCode: 'CEU3133600008', weight: 0.20, clauseLabel: 'Transportation Index Weighting' }
];

// ─── Suppliers ──────────────────────────────────────────────────────────────
// One illustrative slot ("Frame & body supplier") + two TBD slots. The single
// populated supplier uses the verbatim §24.4.5 contract basket as its weights
// (i.e. the programme-level basket — pending MEPPI confirmation of whether
// individual suppliers carry their own per-supplier baskets).

export const SUPPLIERS: SupplierContract[] = [
  {
    id: 'frame-supplier',
    name: 'Frame & body supplier',
    segment: 'Frame & body shells',
    baseDate: '2024-11-15',
    basePrice: 184_500,
    unitLabel: 'per car set',
    nextReviewDate: '2026-06-30',
    contractRef: '[TBD — MEPPI contract reference]',
    reviewCadence: '[TBD — annual / claim-triggered]',
    passThroughPct: 0.90,
    passThroughSource: 'tbd',
    contractDataSource: 'placeholder',
    indices: CONTRACT_BASKET_24_4_5.map(w => ({
      indexCode: w.indexCode,
      weight: w.weight,
      weightSource: 'contract' as const
    })),
    pendingClaim: {
      claimedPct: 0.094,
      receivedDate: '2026-05-08',
      rationale: '[Illustrative pending claim — to be wired to MEPPI claim intake]',
      source: 'placeholder'
    },
    pastClaims: []
  },
  {
    id: 'supplier-slot-b',
    name: '[Supplier slot B — populate from MEPPI roster]',
    segment: '',
    baseDate: '',
    basePrice: 0,
    unitLabel: '',
    nextReviewDate: '',
    contractRef: '',
    reviewCadence: '',
    passThroughPct: 0,
    passThroughSource: 'tbd',
    contractDataSource: 'tbd',
    indices: [],
    pastClaims: []
  },
  {
    id: 'supplier-slot-c',
    name: '[Supplier slot C — populate from MEPPI roster]',
    segment: '',
    baseDate: '',
    basePrice: 0,
    unitLabel: '',
    nextReviewDate: '',
    contractRef: '',
    reviewCadence: '',
    passThroughPct: 0,
    passThroughSource: 'tbd',
    contractDataSource: 'tbd',
    indices: [],
    pastClaims: []
  }
];

// ─── AI insights — derived from real index movement ────────────────────────

/**
 * Brand-flavour copy used inside `insightsFor`. Keeps the function pure
 * (no profile token coupling) while letting MEPPI/demo callers thread
 * their own phrasing through.
 */
export interface InsightCopy {
  /** Basket label used in headlines ("§24.4.5 basket" / "indexed basket"). */
  basketLabel: string;
  /** Body sentence shown when contract data is TBD. */
  tbdBody: string;
  /** Body fragment shown when claim exceeds basket. */
  exceedsBody: string;
  /** Body sentence shown when nextReviewDays > 0. */
  reviewBody: string;
}

export const MEPPI_INSIGHT_COPY: InsightCopy = {
  basketLabel: '§24.4.5 basket',
  tbdBody:
    'Populate base price, base date, agreed indices and per-supplier weights from the MEPPI contract record to enable live insights.',
  exceedsBody:
    'The agreed escalation basket (WPU10 · WPUID61113 · the three AHE labor series, weighted per §24.4.5) has moved {justified}% since the supplier\'s base date. Anything above that is the supplier asking for margin, not pass-through.',
  reviewBody:
    'Any interim renegotiation before that date is voluntary; the supplier has no contractual standing under §24.4 for an unscheduled price change.',
};

export const DEMO_INSIGHT_COPY: InsightCopy = {
  basketLabel: 'indexed basket',
  tbdBody:
    'Populate base price, base date, agreed indices and per-supplier weights from the customer contract record to enable live insights.',
  exceedsBody:
    'The agreed escalation basket (weighted per the indexed-basket schedule) has moved {justified}% since the supplier\'s base date. Anything above that is the supplier asking for margin, not pass-through.',
  reviewBody:
    'Any interim renegotiation before that date is voluntary; the supplier has no contractual standing under the standard escalation clause for an unscheduled price change.',
};

export function insightsFor(
  supplier: SupplierContract,
  justifiedPct: number,
  claimedPct: number,
  nextReviewDays: number,
  copy: InsightCopy = MEPPI_INSIGHT_COPY,
): InsightCard[] {
  if (supplier.contractDataSource === 'tbd') {
    return [{
      tone: 'neutral',
      headline: 'No contract data loaded for this supplier yet',
      body: copy.tbdBody,
    }];
  }
  const cards: InsightCard[] = [];

  if (supplier.pendingClaim) {
    const gap = claimedPct - justifiedPct;
    cards.push({
      tone: gap > 0.5 ? 'warning' : 'positive',
      headline: gap > 0.5
        ? `Claim of ${claimedPct.toFixed(1)}% exceeds ${copy.basketLabel} by ${gap.toFixed(1)}pp`
        : `Claim of ${claimedPct.toFixed(1)}% sits within the ${copy.basketLabel} movement`,
      body: gap > 0.5
        ? copy.exceedsBody.replace('{justified}', justifiedPct.toFixed(1))
        : 'Index movement since base supports the claim. Counter only on small items where it would shift the unit economics.'
    });
  }

  const wpu10 = INDICES.find(i => i.code === 'WPU10')!;
  if (wpu10.series.length > 0 && supplier.indices.some(l => l.indexCode === 'WPU10')) {
    const baseDate = supplier.baseDate;
    const basePt = wpu10.series.find(p => p.date >= baseDate) ?? wpu10.series[0];
    const latest = wpu10.series[wpu10.series.length - 1];
    const wpu10Move = (latest.value / basePt.value - 1) * 100;
    cards.push({
      tone: 'neutral',
      headline: `WPU10 Metals PPI ${wpu10Move >= 0 ? 'up' : 'down'} ${Math.abs(wpu10Move).toFixed(1)}% since base date`,
      body: `Real BLS value ${latest.value.toFixed(1)} (${latest.date}) vs ${basePt.value.toFixed(1)} (${basePt.date}). At a 30% basket weight, this contributes ${(wpu10Move * 0.30).toFixed(2)}pp to the justified-movement total. Source: ${wpu10.sourceUrl}`
    });
  }

  if (nextReviewDays > 0) {
    cards.push({
      tone: 'positive',
      headline: `Next contractual review is ${nextReviewDays} days away`,
      body: copy.reviewBody,
    });
  }
  return cards;
}

// ─── Assumptions surfaced in-UI ─────────────────────────────────────────────
export interface Assumption {
  topic: string;
  source: DataSource;
  detail: string;
}

export const ASSUMPTIONS: Assumption[] = [
  {
    topic: 'Index time series (6 BLS series — 5 in basket + ECI Wages)',
    source: 'bls',
    detail: 'Six series pulled from data.bls.gov. The §24.4.5 basket is made up of five — WPU10, WPUID61113 and the three AHE series (CEU3133530008, CEU3133590008, CEU3133600008). ECI Wages is loaded for context but sits outside the basket. WPU10, WPUID61113 and ECI Wages came via the MEPPI workbook BLS Data sheet (17 May 2026 snapshot); the AHE series were fetched directly on 20 May 2026. The most recent month per series is BLS-flagged "preliminary". No values are synthetic.'
  },
  {
    topic: '§24.4.5 basket weights',
    source: 'contract',
    detail: 'Weights are verbatim from the "Attachment B — Pricing related Terms for Suppliers" workbook, sheet v1, rows 10–14. Sum = 1.0 confirmed in source. These are the programme-level weights; per-supplier weightings would require MEPPI to confirm whether each supplier has its own basket.'
  },
  {
    topic: 'Supplier identities',
    source: 'placeholder',
    detail: 'Only one supplier slot is populated, labelled generically ("Frame & body supplier"). No specific supplier names are invented. The other two slots are explicitly marked TBD until MEPPI provides its supplier roster.'
  },
  {
    topic: 'Contract terms per supplier (base price, base date, review cadence, pass-through %)',
    source: 'placeholder',
    detail: 'Placeholder values. In production these are read from the signed §24.4 schedule per supplier.'
  },
  {
    topic: 'Pending claim figure (9.4%)',
    source: 'placeholder',
    detail: 'Illustrative figure used to seed the reconciler. Production would wire this to MEPPI\'s claim intake channel (email / portal).'
  },
  {
    topic: 'Pass-through scenario brackets (100 / 80 / 60 / 40 %)',
    source: 'derived',
    detail: 'A four-step bracket to expose customer-side recovery sensitivity. The operative pass-through for any given contract comes from the customer-side §24.4 escalation and qualifying-share rules.'
  },
  {
    topic: 'Bid SKU list',
    source: 'placeholder',
    detail: 'Six illustrative SKU rows for layout. Production reads from the MEPPI sourcing team\'s bid SKU file.'
  },
  {
    topic: '"Today" for elapsed-time calculations',
    source: 'derived',
    detail: 'Pinned at 20 May 2026 to align with the latest BLS pull and keep the demo reproducible. Production uses the actual current date.'
  },
  {
    topic: 'Forecast — workbook scenarios (Best / Base / Recent / Worst)',
    source: 'contract',
    detail: 'Annual % change rates per index are verbatim from the MEPPI workbook "Market Scenarios" sheet (rows 6-29). These are MEPPI-aligned scenario constants, not Spendkey projections.'
  },
  {
    topic: 'Forecast — Spendkey statistical projection',
    source: 'derived',
    detail: 'Trailing-CAGR via log-linear OLS on the last 36 monthly BLS observations per index. Annualised growth = exp(slope × 12) − 1. Exposed as an optional second curve for comparison against the contract scenarios; clearly labelled "spendkey" to distinguish it from the contract-aligned scenarios.'
  }
];

// ─── Design decisions ──────────────────────────────────────────────────────
export interface Decision {
  topic: string;
  decision: string;
  backing: string;
}

export const DECISIONS: Decision[] = [
  {
    topic: 'Justified % comes from the §24.4.5 weighted basket — never a single index',
    decision: 'Per-index basket weight × (current/base index value), summed across all five §24.4.5 basket indices. Identical math to the MEPPI Programme Risk and Levers workbook.',
    backing: 'Single source of truth shared with the spreadsheet model — same tbl.ComponentWeight × tbl.CostProj.RawAnnualChange.calc product. Selecting any single index would be fragile and contract-non-compliant.'
  },
  {
    topic: 'Gap is shown in $ per unit, not only %',
    decision: 'Renegotiation surface always renders the gap as percentage points AND dollars per unit.',
    backing: 'A concrete dollar figure converts a percentage debate into a defensible counter-offer. Negotiating in "pp" alone is harder to sign off.'
  },
  {
    topic: 'Pre-fill the reconciler with the pending claim',
    decision: 'When a supplier has a claim on file, pre-populate the reconciler input with their figure.',
    backing: 'Most common entry-flow is "supplier emailed asking for X — what do I tell them?". The dashboard answers that without any typing.'
  },
  {
    topic: 'Rising index = amber, not red',
    decision: 'A rising agreed index is rendered amber (warning), not red (danger).',
    backing: 'A rising index is the expected condition under a working contract — it triggers the agreed escalation mechanism. Red is reserved for genuine anomalies (claim above basket, uncovered SKU, low data confidence).'
  },
  {
    topic: 'Customer pass-through is shown as a four-step bracket',
    decision: 'The Customer pass-through scenarios table (inside the Escalation invoicing calculator) surfaces 100% / 80% / 60% / 40% scenarios side by side.',
    backing: 'Customer-side recovery is rarely 100% in practice — qualifying-share gaps (§4.11.1), timing slips, change-order exclusions (§24.4.10) eat into it. The bracket exposes the sensitivity without re-running the calculation.'
  },
  {
    topic: 'Bid buffer flags low-confidence rows',
    decision: 'SKUs with fewer than 2 historical data points are visually amber-flagged on the data-points column.',
    backing: 'Per the discovery brief: a single purchase point is no trend. We surface the row but flag the low confidence visually rather than hiding it.'
  },
  {
    topic: 'Insights are narrative, not just KPI tiles',
    decision: '1–3 sentences of human-readable narrative per insight card, not just a number.',
    backing: 'The buyer uses these to brief upward and to push back on suppliers. Sentences are paraphrasable; bare numbers require interpretation.'
  },
  {
    topic: 'Every number carries its provenance',
    decision: 'Cards and rows carry small badges: "BLS" for real-data, "contract" for verbatim Attachment B, "placeholder" / "TBD" otherwise.',
    backing: 'Spendkey is selling traceability. A demo that doesn\'t distinguish data from placeholder undermines the same value the product is meant to deliver.'
  }
];

// ─── Bid Buffer — illustrative SKU list ────────────────────────────────────
// Marked clearly as placeholder. The matched-index column references real BLS
// series IDs; the unit-cost figures are stand-ins until the real bid list is
// supplied by MEPPI sourcing.
export const BID_SKUS: BidSku[] = [
  {
    partNumber: '[Placeholder SKU 1]',
    description: 'Aluminium cabinet, 22kW inverter',
    dataPoints: 6,
    matchedIndex: 'CEU3133530008',
    unitCostEstimate: 4_180,
    indexMovement12mo: 1.053,
    coverage: 'covered',
    recommendedBufferPct: 0.0,
    rationale: 'Tracks Electrical Equipment AHE; §24.4-style escalation should recover.'
  },
  {
    partNumber: '[Placeholder SKU 2]',
    description: 'Copper busbar assembly, 1200A',
    dataPoints: 8,
    matchedIndex: 'WPU10',
    unitCostEstimate: 2_950,
    indexMovement12mo: 1.128,
    coverage: 'partial',
    recommendedBufferPct: 0.045,
    rationale: 'WPU10 up double digits YoY; customer may resist full pass-through on copper. Partial buffer recommended.'
  },
  {
    partNumber: '[Placeholder SKU 3]',
    description: 'Specialty fastener kit (titanium)',
    dataPoints: 2,
    matchedIndex: 'WPU10',
    unitCostEstimate: 1_220,
    indexMovement12mo: 1.128,
    coverage: 'uncovered',
    recommendedBufferPct: 0.085,
    rationale: 'Only 2 historical purchases — Limited trend confidence. Custom Ti alloy outside the §24.4.5 basket.'
  },
  {
    partNumber: '[Placeholder SKU 4]',
    description: 'HVAC compressor unit, hermetic',
    dataPoints: 4,
    matchedIndex: 'CEU3133600008',
    unitCostEstimate: 8_640,
    indexMovement12mo: 1.067,
    coverage: 'covered',
    recommendedBufferPct: 0.0,
    rationale: 'Tracks Transportation Equipment AHE. Covered by basket escalation.'
  },
  {
    partNumber: '[Placeholder SKU 5]',
    description: 'Coupling shock absorber, custom polymer',
    dataPoints: 1,
    matchedIndex: '—',
    unitCostEstimate: 1_870,
    indexMovement12mo: 1.0,
    coverage: 'uncovered',
    recommendedBufferPct: 0.12,
    rationale: 'Single data point — no trend visible. Custom polymer formulation, no public index match. Highest discretionary buffer.'
  },
  {
    partNumber: '[Placeholder SKU 6]',
    description: 'Wiring harness, low-voltage',
    dataPoints: 11,
    matchedIndex: 'CEU3133590008',
    unitCostEstimate: 3_400,
    indexMovement12mo: 1.041,
    coverage: 'covered',
    recommendedBufferPct: 0.0,
    rationale: 'Tracks Other Electrical AHE. Covered.'
  }
];

// ─── Identity facts (sources cited in the dashboard footer) ────────────────
export const IDENTITY_FACTS = {
  meppi: {
    fullName: 'Mitsubishi Electric Power Products, Inc.',
    hq: 'Warrendale, Pennsylvania',
    parent: 'Mitsubishi Electric Corporation',
    sourceUrl: 'https://www.meppi.com/about-us'
  },
  spendkey: {
    domain: 'spendkey.io',
    palette: {
      navy: '#032c46',
      cyan: '#00ace2',
      mint: '#5cead2',
      brightBlue: '#00a8fd'
    },
    fontFamily: 'Circular Std, "Proxima Nova", Archivo, "Poppins", sans-serif',
    sourceUrl: 'https://www.spendkey.io'
  }
};
